import { useState, useCallback } from "react";
import { googlePlacesService } from "../../../services/googlePlacesService";
import { Shop } from "../../../types/shop";
import { formatWorkingHours, cleanPhoneNumber } from "../../../utils/shopUtils";

interface GooglePlaceDetailsHook {
  fetchGooglePlaceDetails: (shop: Shop) => Promise<Shop | null>;
  isFetchingDetails: boolean;
  googlePlacesDetails: Map<string, Shop>;
}

export const useGooglePlaceDetails = (): GooglePlaceDetailsHook => {
  const [isFetchingDetails, setIsFetchingDetails] = useState(false);
  const [googlePlacesDetails, setGooglePlacesDetails] = useState<
    Map<string, Shop>
  >(new Map());

  const fetchGooglePlaceDetails = useCallback(
    async (shop: Shop): Promise<Shop | null> => {
      if (!shop.isGooglePlace || !shop.placeId) {
        return shop; // Not a Google Place or no place_id, return as is
      }

      // If shop already has detailed information, return it
      if (shop.phoneNumber || shop.workingHours) {
        return shop; // Already has details
      }

      // Check if we already have details cached
      const cachedDetails = googlePlacesDetails.get(shop.placeId);
      if (cachedDetails) {
        return cachedDetails;
      }

      try {
        setIsFetchingDetails(true);
        console.log(
          `🔍 Fetching details for Google Place: ${shop.name} (${shop.placeId})`
        );

        // Fetch detailed information from Google Places API
        const details = await googlePlacesService.getPlaceDetailsForShop(
          shop.placeId
        );

        // Create updated shop with detailed information using centralized utility
        const updatedShop: Shop = {
          ...shop,
          phoneNumber: cleanPhoneNumber(
            details.formatted_phone_number ||
            details.international_phone_number ||
            shop.phoneNumber
          ),
          address: details.formatted_address || shop.address, // Use formatted address if available
          workingHours: formatWorkingHours(
            details.opening_hours?.weekday_text?.join(", ") || shop.workingHours
          ),
          website: details.website || shop.website,
          isActive: details.opening_hours?.open_now ?? shop.isActive,
        };

        // Cache the detailed information
        setGooglePlacesDetails((prev) =>
          new Map(prev).set(shop.placeId!, updatedShop)
        );

        console.log(`✅ Google Places details fetched for ${shop.name}:`, {
          hasPhone: !!updatedShop.phoneNumber,
          hasHours: !!updatedShop.workingHours,
          hasWebsite: !!updatedShop.website,
        });

        return updatedShop;
      } catch (error) {
        console.error("Error fetching Google Places details:", error);
        return shop;
      } finally {
        setIsFetchingDetails(false);
      }
    },
    [googlePlacesDetails]
  );

  return {
    fetchGooglePlaceDetails,
    isFetchingDetails,
    googlePlacesDetails,
  };
};
